import { useRef, useCallback } from "react";
import { ExportDropdown } from "@components/common/export/export-dropdown";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { useExportAllPaginated } from "@hooks/use-export-all-paginated";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { ExportFormat } from "@utils/file-data-export";
import type { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import { downloadMembersData } from "@utils/file-data-export";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { ExportHandlerOptions, ExportResult } from "@components/common/export/export-dropdown-types";

interface Props {
  /** The entities that are selected */
  selectedEntities: SdbMemberTypes[];

  /** Recognizes on which context the table is being used */
  subjectType: MemberTableSubject;

  /** The name of the context type */
  entityName: string;

  /** True if some entities are selected */
  areEntitiesSelected: boolean;

  /** Team ID (required for team members export all) */
  teamId?: string;

  /** Company ID (required for team members export all) */
  companyId?: string;
}

/** Dropdown to export members in the file format selected by the user */
export function BulkExportMembers({
  subjectType,
  entityName,
  selectedEntities,
  areEntitiesSelected,
  teamId,
  companyId,
}: Props): JSX.Element {
  const tableType = useAppSelector(tableTypeSelector);
  // Cache fetched members to avoid re-fetching on subsequent exports
  const cachedMembersRef = useRef<SdbMemberTypes[] | null>(null);

  // Generic hook for paginated export (use only for team members)
  const { exportAll } = useExportAllPaginated<
    FaroCoreApiTypes.FetchTeamMembersProps,
    FaroCoreApiTypes.TeamMember,
    SdbMemberTypes
  >();

  // team members fetching with caching
  const fetchAllTeamMembers = useCallback(
    async (
      options?: ExportHandlerOptions
    ): Promise<SdbMemberTypes[] | null> => {
      // Return cached members if available
      if (cachedMembersRef.current) {
        return cachedMembersRef.current;
      }

      // Fetch all team members with pagination
      const result = await exportAll({
        fetchParams: {
          companyId: companyId!,
          teamId: teamId!,
        },
        fetchPage: async (params) => {
          const response = await faroCoreApiClient.teams.fetchTeamMembers(
            params
          );
          return { data: response.data, next: response.next };
        },
        transform: (teamMembers) => {
          return teamMembers.map(
            (member): SdbMemberTypes => ({
              ...member.userResponse,
              role: "MEMBER",
            })
          );
        },
        onProgress: options?.onProgress,
        signal: options?.signal,
      });

      // If cancelled, return null
      if (result === null) {
        return null;
      }

      // Cache the result
      cachedMembersRef.current = result;
      return result;
    },
    [companyId, teamId, exportAll]
  );

  // Extract member fetching logic to a separate function
  const getMembersToExport = useCallback(
    async (
      options?: ExportHandlerOptions
    ): Promise<SdbMemberTypes[] | null> => {
      // If entities are selected, return selected members
      if (areEntitiesSelected) {
        return selectedEntities;
      }

      // For team members with pagination support
      if (subjectType === "team" && teamId && companyId) {
        return await fetchAllTeamMembers(options);
      }

      // For non-team members (no pagination), return selected entities
      return selectedEntities;
    },
    [
      areEntitiesSelected,
      selectedEntities,
      subjectType,
      teamId,
      companyId,
      fetchAllTeamMembers,
    ]
  );

  /**
   * Unified export handler for both selected and all members.
   * @returns number of exported entities if succeeded, null if cancelled
   * @throws Error if export fails
   */
  const handleExport = useCallback(
    async (
      format: ExportFormat,
      options?: ExportHandlerOptions
    ): Promise<ExportResult> => {
      const fileName = generateFileName(subjectType, entityName);

      // Determine which members to export
      const membersToExport = await getMembersToExport(options);

      // If cancelled during fetch, return null
      if (membersToExport === null) {
        return null;
      }

      // Export the members
      return await downloadMembersData({
        fileName,
        members: membersToExport,
        subjectType,
        format,
      });
    },
    [entityName, subjectType, getMembersToExport]
  );

  return (
    <ExportDropdown
      trackingProps={{
        dataType: tableType ?? undefined,
        numberOfEntities: selectedEntities.length,
        areEntitiesSelected,
      }}
      exportHandler={handleExport}
      toastMessage={{
        success: "Successfully exported members.",
        error: "Cannot export members. Please try again.",
      }}
      buttonTooltipText="Export members"
    />
  );
}

/** Returns the file name */
function generateFileName(
  subjectType: MemberTableSubject,
  entityName: string
): string {
  switch (subjectType) {
    case "workspace":
      return `Company ${entityName} - Member List`;

    case "project":
      return `Project ${entityName} - Member List`;

    case "group":
      return `Group ${entityName} - Member List`;

    case "team":
    case "workspaceTeam":
      return `${capitalizeFirstLetter(
        TEAM_DISPLAY_NAME
      )} ${entityName} - Member List`;

    case "activeMembers":
      return `Company ${entityName} - Active Members List`;
  }
}
